import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, RefreshCw } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useTranslation } from 'react-i18next';
import { ActionButton, Button } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface Invite {
  _id: string;
  email: string;
  name: string;
  status: 'pending' | 'used' | 'revoked' | 'expired';
  inviteUrl?: string;
  createdAt: string;
  expiresAt: string;
}

const AdminInvites: React.FC = () => {
  const { t } = useTranslation();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: '10', ...(status && { status }) });
      const res = await axios.get(`/invites?${params}`);
      setInvites(res.data.invites);
      setTotalPages(res.data.totalPages);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar convites:', e);
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const { copy } = useCopyToClipboard();

  const revoke = async (invite: Invite) => {
    if (!(await confirm({ message: t('admin.inviteRevokeConfirm'), confirmText: t('common.revoke'), variant: 'danger' }))) return;
    try {
      await axios.post(`/invites/${invite._id}/revoke`);
      notifications.showSuccess('Sucesso', 'Convite revogado');
      fetchInvites();
    } catch (e: any) {
      notifications.showError('Erro', e.response?.data?.message || 'Erro ao revogar');
    }
  };

  const resend = async (invite: Invite) => {
    try {
      const res = await axios.post('/auth/invite', { email: invite.email, name: invite.name });
      if (res.data?.inviteUrl) {
        await copy(res.data.inviteUrl, 'resend', { message: false });
      }
      const sent = res.data?.emailSent;
      notifications.showSuccess('Sucesso', sent ? 'Convite reenviado por e-mail. Link copiado.' : 'Link copiado. E-mail não enviado (configure SendGrid, MailerSend ou SMTP no servidor). Envie o link manualmente.');
    } catch (e: any) {
      notifications.showError('Erro', e.response?.data?.message || 'Erro ao reenviar');
    }
  };

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (inv: (typeof invites)[number]) => (
    <div className="row-actions md:justify-end">
      <ActionButton
        action="copyLink"
        value={inv.inviteUrl}
      />
      <ActionButton
        action="send"
        label="Reenviar"
        onClick={() => resend(inv)}
      />
      {inv.status === 'pending' && (
        <ActionButton
          action="revoke"
          onClick={() => revoke(inv)}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6"><Breadcrumbs /></div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.invites')}</h1>
            <Button onClick={fetchInvites}>
              <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                placeholder="Filtrar por status (pending/used/revoked/expired)"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="field pl-10 pr-4"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-600">Carregando...</div>
          ) : invites.length === 0 ? (
            <div className="p-8 text-center text-gray-600">Nenhum convite encontrado.</div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Convidado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expira</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invites.map((inv) => (
                    <tr key={inv._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{inv.name}</div>
                        <div className="text-sm text-gray-500">{inv.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {inv.status}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(inv.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(inv.expiresAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {renderActions(inv)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {invites.map((inv) => (
                <li key={inv._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{inv.name}</div>
                    <div className="text-sm text-gray-500">{inv.email}</div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {inv.status}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Criado</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {new Date(inv.createdAt).toLocaleString('pt-BR')}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Expira</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {new Date(inv.expiresAt).toLocaleString('pt-BR')}
                      </dd>
                    </div>
                  </dl>
                  {renderActions(inv)}
                </li>
              ))}
            </ul>
            </>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <Button variant="ghost" size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}>
                Anterior
              </Button>
              <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
              <Button variant="ghost" size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}>
                Próxima
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInvites;


